import { MEASUREMENT_DEFAULTS } from "@/api/constants";
import { useState } from "react";
import type { QuizSteps, QuizAnswerValue } from "@/types/api.types";
import UnitToggle, { type UnitSystem } from "@/components/atoms/UnitToggle";
import InputField from "@/components/atoms/InputField";
import PrimaryButton from "@/components/atoms/PrimaryButton";
import QuizTitle from "@/components/atoms/QuizTitle";
import { toMetricHeightCm, toMetricWeightKg } from "@/utils/conversion";

const MeasurementStep = ({
  step,
  onAnswer,
  answers,
  staticLabels,
}: QuizSteps) => {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [unit, setUnit] = useState<UnitSystem>(() => {
    const safeAnswers = answers || {};

    const previousAnswerWithUnit = Object.values(safeAnswers).find(
      (a) => typeof a === "object" && a !== null && "unit" in a,
    ) as { unit: string } | undefined;

    if (previousAnswerWithUnit?.unit === "imperial") return "imperial";
    if (previousAnswerWithUnit?.unit === "metric") return "metric";

    return step.units || "metric";
  });

  const defaults =
    MEASUREMENT_DEFAULTS[step.id] || MEASUREMENT_DEFAULTS.weight_current;

  const placeholderValue =
    unit === "metric" ? defaults.metric : defaults.imperial;
  const label =
    unit === "metric" ? defaults.labelMetric : defaults.labelImperial;

  const getValidationError = (): string | null => {
    const isHeight = step.id === "height";
    const isWeight = step.id.includes("weight");

    // base validation
    if (isHeight) {
      const cm = toMetricHeightCm(inputValue, unit);
      if (cm == null || cm < 100 || cm > 250) {
        return (
          (unit === "metric"
            ? step.validation?.errorMetric
            : step.validation?.errorImperial) ?? null
        );
      }
    }

    if (isWeight) {
      const kg = toMetricWeightKg(inputValue, unit);
      if (kg == null || kg <= 40) {
        return (
          (unit === "metric"
            ? step.validation?.errorMetric
            : step.validation?.errorImperial) ?? null
        );
      }
    }

    if (step.id === "weight_goal" && step.validation?.errorLogic) {
      const current = answers?.["weight_current"] as
        | QuizAnswerValue
        | undefined;

      let currentKg: number | null = null;

      if (
        current &&
        typeof current === "object" &&
        "value" in current &&
        "unit" in current
      ) {
        currentKg = toMetricWeightKg(
          String(current.value),
          current.unit as UnitSystem,
        );
      } else if (current != null) {
        currentKg = toMetricWeightKg(String(current), unit);
      }

      const goalKg = toMetricWeightKg(inputValue, unit);

      if (currentKg != null && goalKg != null && goalKg >= currentKg) {
        return step.validation.errorLogic;
      }
    }

    return null;
  };

  const handleContinue = () => {
    const msg = getValidationError();
    if (msg) {
      setError(msg);
      return;
    }

    setError(null);
    onAnswer({
      value: inputValue,
      unit,
    });
  };

  return (
    <div className="mx-auto w-full">
      <QuizTitle title={step.title} className="pb-9" />

      <div className="mb-4 flex justify-center">
        <UnitToggle
          value={unit}
          onChange={(u) => {
            setUnit(u);
            setError(null);
          }}
          className="max-w-[400px]"
          metricLabel={staticLabels?.metricLabel}
          imperialLabel={staticLabels?.imperialLabel}
        />
      </div>

      <div className="mb-14 flex justify-center">
        <div className="w-full max-w-[400px]">
          <InputField
            placeholder={`e.g. ${placeholderValue} ${label}`}
            value={inputValue}
            onChange={(v) => {
              setInputValue(v);
              if (error) setError(null);
            }}
            unit={label}
            error={error ?? undefined}
          />
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <div className="w-full">
          {step.options?.map((option) => (
            <PrimaryButton
              key={option.id}
              label={option.label}
              disabled={!inputValue}
              onClick={handleContinue}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MeasurementStep;
